import { format } from "date-fns"
import { getFlagsPalette } from "design-system/lib/chart-utils"
import { Card } from "design-system/molecules/Card"
import { NoData } from "design-system/molecules/NoData"
import { Loading } from "modules/shared/components/Loading"
import { ReactNode, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { BACKOFFICE_FLAGS_RANKING_TOP_N, BackofficeFlagsTimelineResponseDataSchema } from "schema"
import { z } from "zod"

type TimelineData = z.infer<typeof BackofficeFlagsTimelineResponseDataSchema>
type TimelineRanking = TimelineData["timeline"][number]["rankings"][number]

type Props = {
  data: TimelineData | undefined
  isPending: boolean
}

type TooltipPayload = {
  color?: string
  name?: string | number
  value?: string | number | readonly (string | number)[]
}

const getRankingId = (key: string, kind: TimelineRanking["kind"]) => `${kind}\0${key}`

function RankingTooltip({
  active,
  label,
  payload,
}: {
  active?: boolean
  label?: ReactNode
  payload?: readonly TooltipPayload[]
}) {
  if (!active || !payload?.length) return null

  const rankings = [...payload]
    .filter((item) => typeof item.value === "number")
    .sort((a, b) => Number(a.value) - Number(b.value))

  return (
    <div className="border-border bg-background min-w-52 rounded-lg border p-3 shadow-xl">
      <div className="mb-2 text-xs font-medium">{label}</div>
      <div className="flex flex-col gap-1.5">
        {rankings.map((item) => (
          <div key={String(item.name)} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="truncate">{item.name}</span>
            </div>
            <span className="tabular-nums">
              {"#"}
              {Number(item.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function BackofficeFlagsTimeline({ data, isPending }: Props) {
  const { t } = useTranslation()
  const latestRanking = useMemo(
    () => [...(data?.timeline ?? [])].reverse().find((period) => period.rankings.length > 0),
    [data?.timeline],
  )

  const series = useMemo(() => {
    if (!data || !latestRanking) return []

    const latestRankings = latestRanking.rankings.slice().sort((a, b) => a.rank - b.rank)
    const latestRankingIds = new Set(
      latestRankings.map((ranking) => getRankingId(ranking.key, ranking.kind)),
    )
    const historicalRankings = new Map<string, TimelineRanking>()

    for (const period of data.timeline) {
      for (const ranking of period.rankings) {
        const rankingId = getRankingId(ranking.key, ranking.kind)
        if (!latestRankingIds.has(rankingId)) historicalRankings.set(rankingId, ranking)
      }
    }

    const overallRankById = new Map(
      data.overallRanking.map((ranking) => [getRankingId(ranking.key, ranking.kind), ranking.rank]),
    )
    const remainingRankings = [...historicalRankings.entries()]
      .sort(
        (a, b) =>
          (overallRankById.get(a[0]) ?? Number.MAX_SAFE_INTEGER) -
            (overallRankById.get(b[0]) ?? Number.MAX_SAFE_INTEGER) ||
          a[1].key.localeCompare(b[1].key),
      )
      .map(([, ranking]) => ranking)
    const rankings = [...latestRankings, ...remainingRankings]
    const palette = getFlagsPalette(rankings.length)

    return rankings.map((ranking, index) => ({
      key: ranking.key,
      kind: ranking.kind,
      rankingId: getRankingId(ranking.key, ranking.kind),
      dataKey: `key${index}`,
      color: palette[index] ?? "var(--neutral)",
    }))
  }, [data, latestRanking])

  const colorByRankingId = useMemo(
    () => Object.fromEntries(series.map(({ rankingId, color }) => [rankingId, color])),
    [series],
  )

  const chartData = useMemo(
    () =>
      (data?.timeline ?? []).map((period) => {
        const row: Record<string, string | number | null> = {
          period: format(new Date(period.date), "MMM d"),
        }

        for (const { key, kind, dataKey } of series) {
          row[dataKey] =
            period.rankings.find((ranking) => ranking.key === key && ranking.kind === kind)?.rank ??
            null
        }

        return row
      }),
    [data?.timeline, series],
  )

  return (
    <Card className="flex flex-col gap-4 p-6">
      {isPending && <Loading />}

      {!isPending && series.length === 0 && (
        <NoData
          frame
          title={t("backoffice.flags.timeline.noData.title")}
          description={t("backoffice.flags.timeline.noData.description")}
        />
      )}

      {!isPending && series.length > 0 && (
        <div className="flex w-full flex-col gap-4 lg:flex-row">
          <div className="h-[250px] min-w-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                <XAxis
                  dataKey="period"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={10}
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={false}
                  axisLine={false}
                  domain={[1, BACKOFFICE_FLAGS_RANKING_TOP_N]}
                  reversed
                  padding={{ bottom: 10 }}
                  width={0}
                />
                <Tooltip content={<RankingTooltip />} />
                {series.map(({ color, dataKey, key, rankingId }) => (
                  <Line
                    key={rankingId}
                    type="linear"
                    dataKey={dataKey}
                    name={key}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 4, fill: color, stroke: color, strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: color, stroke: color }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="border-border flex w-full shrink-0 flex-col gap-2 rounded-md border border-dashed p-4 lg:w-sm">
            <div className="text-muted-foreground mb-1 text-xs">
              {latestRanking
                ? t("backoffice.flags.timeline.latestRanking", {
                    date: format(new Date(latestRanking.date), "MMM d"),
                  })
                : null}
            </div>
            {latestRanking?.rankings.map((ranking) => (
              <div
                key={getRankingId(ranking.key, ranking.kind)}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <div
                    className="flex size-5 shrink-0 items-center justify-center rounded-sm"
                    style={{
                      backgroundColor: colorByRankingId[getRankingId(ranking.key, ranking.kind)],
                    }}
                  >
                    <span className="text-background text-[10px] font-semibold">
                      {"#"}
                      {ranking.rank}
                    </span>
                  </div>
                  <span className="truncate text-sm">{ranking.key}</span>
                </div>
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                  {t("backoffice.flags.timeline.flags", { count: ranking.count })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}
